import { supabase } from '../lib/supabase';
import { hasValidPositionData } from './positionExtraction';

export type OperationStatus = 'synced' | 'sync_required' | 'running' | 'error';

export interface OperationStatusResult {
  status: OperationStatus;
  errorMessage?: string;
  lastChecked?: Date;
}

/**
 * Calculate sync status by comparing scoresheet_logs vs destination table
 */
async function calculateSyncStatus(
  matchId: string,
  destinationTable: 'key_match_events' | 'match_timeline' | 'match_visualization' | 'match_video_tags',
  filterFn?: (log: any) => boolean
): Promise<OperationStatusResult> {
  try {
    // Get all relevant scoresheet_logs for this match
    // Include action, new_value, and position_data for filtering
    const { data: logs, error: logsError } = await supabase
      .from('scoresheet_logs')
      .select(`
        id,
        action,
        new_value,
        position_data,
        stat_types!inner(stat_key, display_name, category, event_result)
      `)
      .eq('match_id', matchId)
      .eq('is_active', true);

    if (logsError) {
      console.error(`Error fetching scoresheet logs for ${destinationTable} status:`, logsError);
      // Return error status instead of throwing, so UI can show the error
      return {
        status: 'error',
        errorMessage: `Failed to fetch logs: ${logsError.message}`,
        lastChecked: new Date()
      };
    }

    if (!logs || logs.length === 0) {
      return { status: 'synced', lastChecked: new Date() };
    }

    // Apply filter if provided
    const relevantLogs = filterFn ? logs.filter(filterFn) : logs;

    if (relevantLogs.length === 0) {
      return { status: 'synced', lastChecked: new Date() };
    }

    const logIds = relevantLogs.map(log => log.id).filter(Boolean);

    if (logIds.length === 0) {
      return { status: 'synced', lastChecked: new Date() };
    }

    // Get all existing records in destination table
    // Note: We check for existence regardless of status (published/unpublished) or is_active
    // If a record exists with source_log_id, it's considered synced
    // We don't filter by is_active because decrements create inactive records, but they're still "synced"
    let existingRecords: any[] = [];
    
    // Handle large arrays by chunking (Supabase has limits on .in() array size)
    // Use smaller chunk size for match_visualization to avoid Bad Request errors
    const chunkSize = destinationTable === 'match_visualization' ? 500 : 1000;
    for (let i = 0; i < logIds.length; i += chunkSize) {
      const chunk = logIds.slice(i, i + chunkSize);
      
      // Skip empty chunks
      if (chunk.length === 0) {
        continue;
      }
      
      const { data, error } = await supabase
        .from(destinationTable)
        .select('source_log_id')
        .eq('match_id', matchId)
        .in('source_log_id', chunk);
      
      if (error) {
        console.error(`Error fetching existing records from ${destinationTable}:`, error);
        console.error(`Query details: matchId=${matchId}, chunkSize=${chunk.length}, totalLogIds=${logIds.length}`);
        // Return error status instead of throwing, so UI can show the error
        return {
          status: 'error',
          errorMessage: `Failed to check sync status: ${error.message}`,
          lastChecked: new Date()
        };
      }
      
      if (data) {
        existingRecords = existingRecords.concat(data);
      }
    }

    // Create a set of synced log IDs
    const syncedLogIds = new Set(
      (existingRecords || [])
        .map(r => r.source_log_id)
        .filter(Boolean)
    );

    // Check if all logs have corresponding records
    const unsyncedLogs = relevantLogs.filter(log => !syncedLogIds.has(log.id));

    return {
      status: unsyncedLogs.length > 0 ? 'sync_required' : 'synced',
      lastChecked: new Date()
    };
  } catch (error) {
    return {
      status: 'error',
      errorMessage: error instanceof Error ? error.message : 'Unknown error',
      lastChecked: new Date()
    };
  }
}

/**
 * Calculate status for Match Stats calculation
 * Compares latest scoresheet_logs timestamp vs last calculation
 */
export async function calculateMatchStatsStatus(
  matchId: string,
  homeTeamId: string,
  awayTeamId: string
): Promise<OperationStatusResult> {
  try {
    // Get latest scoresheet_logs timestamp (use timestamp field which is the event time)
    const { data: latestLog, error: logError } = await supabase
      .from('scoresheet_logs')
      .select('timestamp')
      .eq('match_id', matchId)
      .eq('is_active', true)
      .order('timestamp', { ascending: false })
      .limit(1)
      .single();

    if (logError && logError.code !== 'PGRST116') { // PGRST116 = no rows returned
      throw new Error(`Failed to fetch latest log: ${logError.message}`);
    }

    if (!latestLog) {
      return { status: 'synced', lastChecked: new Date() };
    }

    // Get last calculation timestamp from scoresheet table
    // We'll check the updated_at of calculated stats for both teams
    const { data: scoresheetData, error: scoresheetError } = await supabase
      .from('scoresheet')
      .select('updated_at')
      .eq('match_id', matchId)
      .in('team_id', [homeTeamId, awayTeamId])
      .order('updated_at', { ascending: false })
      .limit(1)
      .single();

    if (scoresheetError && scoresheetError.code !== 'PGRST116') {
      throw new Error(`Failed to fetch scoresheet data: ${scoresheetError.message}`);
    }

    // If no scoresheet data exists, calculation is required
    if (!scoresheetData) {
      return { status: 'sync_required', lastChecked: new Date() };
    }

    // Compare timestamps
    const logTimestamp = new Date(latestLog.timestamp).getTime();
    const calculationTimestamp = new Date(scoresheetData.updated_at).getTime();

    return {
      status: logTimestamp > calculationTimestamp ? 'sync_required' : 'synced',
      lastChecked: new Date()
    };
  } catch (error) {
    return {
      status: 'error',
      errorMessage: error instanceof Error ? error.message : 'Unknown error',
      lastChecked: new Date()
    };
  }
}

/**
 * Calculate status for Player Stats calculation
 * Similar to Match Stats - compares timestamps
 */
export async function calculatePlayerStatsStatus(matchId: string): Promise<OperationStatusResult> {
  try {
    // Get latest scoresheet_logs timestamp (use timestamp field which is the event time)
    const { data: latestLog, error: logError } = await supabase
      .from('scoresheet_logs')
      .select('timestamp')
      .eq('match_id', matchId)
      .eq('is_active', true)
      .order('timestamp', { ascending: false })
      .limit(1)
      .single();

    if (logError && logError.code !== 'PGRST116') {
      throw new Error(`Failed to fetch latest log: ${logError.message}`);
    }

    if (!latestLog) {
      return { status: 'synced', lastChecked: new Date() };
    }

    // Check if there are any logs with player_id - if not, player stats are not needed
    const { data: logsWithPlayer, error: logsWithPlayerError } = await supabase
      .from('scoresheet_logs')
      .select('id')
      .eq('match_id', matchId)
      .eq('is_active', true)
      .not('player_id', 'is', null)
      .limit(1)
      .single();

    if (logsWithPlayerError && logsWithPlayerError.code !== 'PGRST116') {
      // If error other than "no rows", log it but continue
      console.warn('Error checking for logs with player_id:', logsWithPlayerError);
    }

    // If no logs have player_id, player stats are not needed - return synced
    if (!logsWithPlayer) {
      return { status: 'synced', lastChecked: new Date() };
    }

    // Get last calculation timestamp from player_scoresheet table
    const { data: playerStats, error: playerStatsError } = await supabase
      .from('player_scoresheet')
      .select('updated_at')
      .eq('match_id', matchId)
      .order('updated_at', { ascending: false })
      .limit(1)
      .single();

    if (playerStatsError && playerStatsError.code !== 'PGRST116') {
      throw new Error(`Failed to fetch player stats: ${playerStatsError.message}`);
    }

    // If no player stats exist but we have logs with player_id, calculation is required
    if (!playerStats) {
      return { status: 'sync_required', lastChecked: new Date() };
    }

    // Compare timestamps
    const logTimestamp = new Date(latestLog.timestamp).getTime();
    const statsTimestamp = new Date(playerStats.updated_at).getTime();

    return {
      status: logTimestamp > statsTimestamp ? 'sync_required' : 'synced',
      lastChecked: new Date()
    };
  } catch (error) {
    return {
      status: 'error',
      errorMessage: error instanceof Error ? error.message : 'Unknown error',
      lastChecked: new Date()
    };
  }
}

/**
 * Calculate status for Timeline sync
 */
export async function calculateTimelineStatus(matchId: string): Promise<OperationStatusResult> {
  return calculateSyncStatus(matchId, 'match_timeline', (log) => {
    const statType = log.stat_types as any;
    // Exclude certain stat types (same as syncMatchTimeline)
    const excluded = [
      'short_pass_success',
      'short_pass_failure',
      'short_pass_backward_success',
      'short_pass_backward_failure',
      'long_pass_success',
      'long_pass_failure',
      'long_pass_backward_success',
      'long_pass_backward_failure',
      'tackle_success',
      'tackle_failure',
      'throw_in',
      'interception',
      'clearance'
    ];
    return !excluded.includes(statType.stat_key);
  });
}

/**
 * Calculate status for Key Events sync
 */
export async function calculateKeyEventsStatus(matchId: string): Promise<OperationStatusResult> {
  // Key event stat types (matching KeyEventsSyncService.KEY_EVENT_STAT_TYPES)
  const KEY_EVENT_STAT_TYPES = [
    'shot_inside_box_goal',
    'shot_outside_box_goal',
    'penalty_scored',
    'penalty_missed',
    'penalty_shootout_scored',
    'penalty_shootout_missed',
    'own_goal',
    'assist',
    'yellow_card',
    'red_card',
    'substitution',
    'save_open_play',
    'save_penalty',
    'penalty_punch',
    'penalty_claim',
    'open_play_punch',
    'open_play_claim',
    'kick_off',
    'end_1st_half',
    'start_2nd_half',
    'start_extra_time_1st_half',
    'end_extra_time_1st_half',
    'start_extra_time_2nd_half',
    'penalty_shootout',
    'full_time'
  ];

  // Replicate isKeyEvent() logic from KeyEventsSyncService
  const isKeyEvent = (statKey: string, action: string, value: number): boolean => {
    // Game flow events are always key events
    if (['kick_off', 'end_1st_half', 'start_2nd_half', 'full_time', 'start_extra_time_1st_half', 'end_extra_time_1st_half', 'start_extra_time_2nd_half', 'penalty_shootout'].includes(statKey)) {
      return true;
    }
    // Goals are always key events
    if (['shot_inside_box_goal', 'shot_outside_box_goal', 'penalty_scored'].includes(statKey)) {
      return true;
    }
    // Assists are key events
    if (statKey === 'assist') {
      return true;
    }
    // Own goals are key events
    if (statKey === 'own_goal') {
      return true;
    }
    // Penalty misses are key events
    if (statKey === 'penalty_missed') {
      return true;
    }
    // Penalty shootout stats are key events
    if (['penalty_shootout_scored', 'penalty_shootout_missed'].includes(statKey)) {
      return true;
    }
    // Cards are key events
    if (['yellow_card', 'red_card'].includes(statKey)) {
      return true;
    }
    // Substitutions are key events
    if (statKey === 'substitution') {
      return true;
    }
    // Saves are key events
    if (['save_open_play', 'save_penalty', 'penalty_punch', 'penalty_claim'].includes(statKey)) {
      return true;
    }
    return false;
  };

  return calculateSyncStatus(matchId, 'key_match_events', (log) => {
    const statType = log.stat_types as any;
    // First check if it's in the stat types list
    if (!KEY_EVENT_STAT_TYPES.includes(statType.stat_key)) {
      return false;
    }
    // Then check if it qualifies as a key event using the same logic as sync
    return isKeyEvent(statType.stat_key, log.action || 'increment', log.new_value || 0);
  });
}

/**
 * Calculate status for Visualisation sync
 */
export async function calculateVisualisationStatus(matchId: string): Promise<OperationStatusResult> {
  return calculateSyncStatus(matchId, 'match_visualization', (log) => {
    // Only logs with valid position_data AND only increment actions (matching syncMatchVisualization)
    // Use the same validation function as the sync operation
    const hasValidPosition = hasValidPositionData(log.position_data);
    const isIncrement = log.action === 'increment';
    return hasValidPosition && isIncrement;
  });
}

/**
 * Calculate status for Video Tags sync
 */
export async function calculateVideoTagsStatus(matchId: string): Promise<OperationStatusResult> {
  try {
    // Get logs with video timing
    // Include action field for filtering increment actions
    const { data: logs, error: logsError } = await supabase
      .from('scoresheet_logs')
      .select(`
        id,
        action,
        video_time_seconds,
        video_id,
        manual_tags,
        stat_types!inner(stat_key, display_name, category, event_result)
      `)
      .eq('match_id', matchId)
      .eq('is_active', true)
      .not('video_time_seconds', 'is', null)
      .not('video_id', 'is', null);

    if (logsError) {
      console.error('Error fetching scoresheet logs for video tags status:', logsError);
      // Return error status instead of throwing
      return {
        status: 'error',
        errorMessage: `Failed to fetch logs: ${logsError.message}`,
        lastChecked: new Date()
      };
    }

    if (!logs || logs.length === 0) {
      return { status: 'synced', lastChecked: new Date() };
    }

    // Filter logs (same logic as VideoTagsSyncService)
    const excludedStatTypes = [
      'short_pass_success',
      'short_pass_failure',
      'short_pass_backward_success',
      'short_pass_backward_failure',
      'long_pass_success',
      'long_pass_failure',
      'long_pass_backward_success',
      'long_pass_backward_failure',
      'tackle_success',
      'tackle_failure',
      'throw_in',
      'interception',
      'clearance'
    ];

    // Filter logs (same logic as VideoTagsSyncService)
    // 1. Must have video timing (already filtered in query)
    // 2. Only increment actions (matching syncVideoTagsFromScoresheetLogs)
    // 3. Either: NOT in excluded stat types OR has manual_tags
    const relevantLogs = logs.filter(log => {
      // Only increment actions (matching sync operation)
      if (log.action !== 'increment') {
        return false;
      }
      
      const statType = log.stat_types as any;
      const statKey = statType?.stat_key;
      
      const hasManualTags = log.manual_tags && 
        Array.isArray(log.manual_tags) && 
        log.manual_tags.length > 0;
      
      const isExcluded = excludedStatTypes.includes(statKey);
      
      return !isExcluded || hasManualTags;
    });

    if (relevantLogs.length === 0) {
      return { status: 'synced', lastChecked: new Date() };
    }

    const logIds = relevantLogs.map(log => log.id).filter(Boolean);

    // Get existing video tags
    const { data: existingTags, error: existingError } = await supabase
      .from('match_video_tags')
      .select('source_log_id')
      .eq('match_id', matchId)
      .in('source_log_id', logIds);

    if (existingError) {
      console.error('Error fetching existing video tags:', existingError);
      // Return error status instead of throwing
      return {
        status: 'error',
        errorMessage: `Failed to check sync status: ${existingError.message}`,
        lastChecked: new Date()
      };
    }

    const syncedLogIds = new Set(
      (existingTags || [])
        .map(t => t.source_log_id)
        .filter(Boolean)
    );

    const unsyncedLogs = relevantLogs.filter(log => !syncedLogIds.has(log.id));

    return {
      status: unsyncedLogs.length > 0 ? 'sync_required' : 'synced',
      lastChecked: new Date()
    };
  } catch (error) {
    return {
      status: 'error',
      errorMessage: error instanceof Error ? error.message : 'Unknown error',
      lastChecked: new Date()
    };
  }
}

